import React, { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Icon } from '../Icon';
import { Button } from '../Button';
import { timezones, TimezoneOption } from '../../utils/timezones';
import './TimezoneSelector.css';

interface TimezoneSelectorProps {
  currentTimezone?: string;
  onSelect: (timezone: string, timezoneCity: string) => void;
  onClose: () => void;
  isUpdating?: boolean;
}

export const TimezoneSelector: React.FC<TimezoneSelectorProps> = ({
  currentTimezone,
  onSelect,
  onClose,
  isUpdating = false
}) => {
  const { t } = useTranslation();
  const [searchQuery, setSearchQuery] = useState('');

  // Создаем плоский список всех городов для поиска
  const cityList = useMemo(() => {
    const list: Array<{ city: string; timezone: TimezoneOption }> = [];
    
    timezones.forEach(tz => {
      // Добавляем основной label
      list.push({ city: tz.label, timezone: tz });
      
      // Добавляем все города из массива cities
      if (tz.cities) {
        tz.cities.forEach(city => {
          list.push({ city, timezone: tz });
        });
      }
    });
    
    return list;
  }, []);

  // Фильтруем города по поисковому запросу
  const filteredResults = useMemo(() => {
    if (!searchQuery.trim()) {
      // Если нет поиска, показываем основные timezone без дубликатов
      return timezones;
    }

    const query = searchQuery.toLowerCase().trim();
    const matchedCities = cityList.filter(item =>
      item.city.toLowerCase().includes(query)
    );

    // Удаляем дубликаты timezone
    const uniqueTimezones = new Map<string, { city: string; timezone: TimezoneOption }>();
    matchedCities.forEach(item => {
      if (!uniqueTimezones.has(item.timezone.value)) {
        uniqueTimezones.set(item.timezone.value, item);
      }
    });

    return Array.from(uniqueTimezones.values());
  }, [searchQuery, cityList]);

  const handleSelect = (result: TimezoneOption | { city: string; timezone: TimezoneOption }) => {
    if ('timezone' in result) {
      // Это результат с конкретным городом из поиска
      onSelect(result.timezone.value, result.city);
    } else {
      // Это просто timezone
      onSelect(result.value, result.label);
    }
  };

  return (
    <div 
      className="timezone-selector-overlay"
      onClick={() => !isUpdating && onClose()}
    >
      <div
        className="timezone-selector-modal"
        onClick={(e) => e.stopPropagation()}
      >
        <h3 className="timezone-selector__title">
          {t('timezone.selectTimezone')}
        </h3>
        
        {/* Search Input */}
        <div className="timezone-selector__search">
          <Icon name="search" size={20} color="var(--color-text-secondary)" />
          <input
            type="text"
            className="timezone-selector__search-input"
            placeholder={t('timezone.searchPlaceholder')}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
          {searchQuery && (
            <button
              className="timezone-selector__clear"
              onClick={() => setSearchQuery('')}
            >
              <Icon name="close" size={18} color="var(--color-text-secondary)" />
            </button>
          )}
        </div>

        {/* Results */}
        <div className="timezone-selector__results">
          {filteredResults.length === 0 ? (
            <div className="timezone-selector__no-results">
              <Icon name="search" size={48} color="var(--color-text-tertiary)" />
              <p>{t('timezone.noResults')}</p>
              <p style={{ fontSize: '14px', color: 'var(--color-text-secondary)' }}>
                {t('timezone.tryAnotherCity')}
              </p>
            </div>
          ) : (
            <>
              {!searchQuery && (
                <div className="timezone-selector__hint">
                  {t('timezone.popularCities')}
                </div>
              )}
              {filteredResults.map((result, index) => {
                const tz = 'timezone' in result ? result.timezone : result;
                const cityName = 'city' in result ? result.city : result.label;
                const isSelected = currentTimezone === tz.value;
                const isSearchResult = 'city' in result && searchQuery.trim();
                const mainCity = tz.label;

                return (
                  <button
                    key={`${tz.value}-${index}`}
                    onClick={() => handleSelect(result)}
                    disabled={isUpdating}
                    className={`timezone-selector__item ${isSelected ? 'timezone-selector__item--selected' : ''}`}
                  >
                    <div className="timezone-selector__item-main">
                      <div className="timezone-selector__item-city">
                        {cityName}
                      </div>
                      <div className="timezone-selector__item-offset">
                        {isSearchResult && cityName !== mainCity ? (
                          <span>
                            {mainCity}, {tz.offset}
                          </span>
                        ) : (
                          tz.offset
                        )}
                      </div>
                    </div>
                    {isSelected && (
                      <div className="timezone-selector__item-check">
                        <Icon name="check" size={20} color="var(--color-primary)" />
                      </div>
                    )}
                  </button>
                );
              })}
            </>
          )}
        </div>

        {/* Cancel Button */}
        <Button 
          variant="secondary" 
          onClick={onClose}
          className="timezone-selector__cancel"
          disabled={isUpdating}
          fullWidth
        >
          {t('common.cancel')}
        </Button>
      </div>
    </div>
  );
};

